// Shared constants + sanitizers for assigning the two practitioner auditor roles.
// Ported from the Control Center "Assign Role" flow (RoleAssignmentDialog.jsx /
// ProfileFormRenderer.jsx) so the support-portal payloads match what the backend
// AuditorProfileService expects.

export const AUDITOR_ROLE_OPTIONS = [
  { id: 'ROLE_THERAPY_AUDITOR', label: 'Therapy Auditor', defaultEventType: 'therapy-session' },
  { id: 'ROLE_SCREENING_AUDITOR', label: 'Screening Auditor', defaultEventType: 'screening' },
]

export const AUDITOR_ROLE_LABELS = AUDITOR_ROLE_OPTIONS.reduce((acc, option) => {
  acc[option.id] = option.label
  return acc
}, {})

export const AUDITOR_ROLE_EVENT_DEFAULTS = AUDITOR_ROLE_OPTIONS.reduce((acc, option) => {
  acc[option.id] = option.defaultEventType
  return acc
}, {})

export const EVENT_TYPE_OPTIONS = [
  { id: 'therapy-session', title: 'Therapy Session' },
  { id: 'screening', title: 'Screening' },
]

export const AUDITOR_TYPE_OPTIONS = [
  { id: 'SUPERVISOR', label: 'Supervisor' },
  { id: 'LEARNER', label: 'Learner' },
]

export function normalizeStringArray(values) {
  if (!Array.isArray(values)) return []
  return values.filter((value) => typeof value === 'string' && value.trim())
}

export function normalizeAuditorRoleIds(values, fallbackRoleId) {
  const roleIds = [...(Array.isArray(values) ? values : []), fallbackRoleId]
    .map((roleId) => String(roleId || '').trim().toUpperCase())
    .filter((roleId) => AUDITOR_ROLE_EVENT_DEFAULTS[roleId])
  return [...new Set(roleIds)]
}

// Stored auditor expiry is UTC "YYYY-MM-DD HH:MM:SS". Convert to/from a datetime-local
// input value (which is local wall-clock, no timezone).
export function toDateTimeLocal(value) {
  if (!value) return ''
  const normalized = String(value).trim()
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(normalized)
    ? `${normalized.replace(' ', 'T')}Z`
    : normalized
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (part) => String(part).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

export function toAuditorDateTimeString(localValue) {
  if (!localValue) return ''
  const date = new Date(localValue)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (part) => String(part).padStart(2, '0')
  return (
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
    `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
  )
}

export function normalizeRoleExpiry(value) {
  if (!value) return ''
  const normalized = String(value).trim()
  const utcInput = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(normalized)
    ? `${normalized.replace(' ', 'T')}Z`
    : normalized
  const date = new Date(utcInput)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (part) => String(part).padStart(2, '0')
  return (
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
    `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
  )
}

export function defaultAuditorPermissions(roleIds) {
  return normalizeStringArray(roleIds)
    .map((roleId) => String(roleId).trim().toUpperCase())
    .filter((roleId) => AUDITOR_ROLE_EVENT_DEFAULTS[roleId])
    .map((roleId) => ({
      roleId,
      eventType: AUDITOR_ROLE_EVENT_DEFAULTS[roleId],
      defaultAuditorType: 'SUPERVISOR',
      roleExpiry: '',
      enabled: true,
    }))
}

// Final sanitize before submit: lowercase event types, uppercase roles/types, drop rows
// for roles not in the allow-list, normalize optional expiry.
export function normalizeAuditorPermissions(permissions, roleIds) {
  const source =
    Array.isArray(permissions) && permissions.length > 0
      ? permissions
      : defaultAuditorPermissions(roleIds)

  return source
    .map((permission) => {
      const eventType = String(permission?.eventType || '').trim().toLowerCase()
      const roleId = String(permission?.roleId || '').trim().toUpperCase()
      if (!eventType || !AUDITOR_ROLE_EVENT_DEFAULTS[roleId]) return null
      const roleExpiry = normalizeRoleExpiry(permission?.roleExpiry)
      return {
        eventType,
        roleId,
        defaultAuditorType: String(permission?.defaultAuditorType || 'SUPERVISOR').trim().toUpperCase(),
        ...(roleExpiry ? { roleExpiry } : {}),
        enabled: permission?.enabled !== false,
      }
    })
    .filter(Boolean)
}
